/**
 * WaterScope Plus RBAC role definitions for Phase 2a.
 *
 * These are the Cognito groups assignable to customer users — they are NOT
 * the Wellspring-internal Permission enum. Keep this file free of any
 * server-only or frontend-only imports.
 */

/** All assignable RBAC roles (Cognito group names). */
export const CUSTOMER_RBAC_ROLES = {
  UtilityAdmin: 'utility-admin',
  MHPAdmin: 'mhp-admin',
  MHPPropertyMgr: 'mhp-property-mgr',
  CMFIAdmin: 'cmfi-admin',
  CMFIPropertyMgr: 'cmfi-property-mgr',
} as const;

export type CustomerRbacRole = typeof CUSTOMER_RBAC_ROLES[keyof typeof CUSTOMER_RBAC_ROLES];

/**
 * Human-readable labels for display in the UI.
 */
export const CUSTOMER_RBAC_ROLE_LABELS: Record<CustomerRbacRole, string> = {
  'utility-admin': 'Admin',
  'mhp-admin': 'Admin',
  'mhp-property-mgr': 'Property Manager',
  'cmfi-admin': 'Admin',
  'cmfi-property-mgr': 'Property Manager',
};

/**
 * Roles available for each account-type combination.
 * The first element is the default (admin) role.
 */
export const ROLES_BY_ACCOUNT_TYPE: Record<string, CustomerRbacRole[]> = {
  Utility: ['utility-admin'],
  'Real Estate:MHP': ['mhp-admin', 'mhp-property-mgr'],
  'Real Estate:CMFI': ['cmfi-admin', 'cmfi-property-mgr'],
};

/**
 * Returns the list of selectable roles for a customer, or an empty array when
 * the account type cannot be mapped. Only account types with more than one
 * role need a picker in the UI.
 */
export function rolesForCustomer(
  accountType: string | null | undefined,
  realEstateAccountType?: string | null,
): CustomerRbacRole[] {
  if (accountType === 'Utility') return ROLES_BY_ACCOUNT_TYPE['Utility'];
  if (accountType === 'Real Estate') {
    if (realEstateAccountType === 'MHP') return ROLES_BY_ACCOUNT_TYPE['Real Estate:MHP'];
    if (realEstateAccountType === 'CMFI') return ROLES_BY_ACCOUNT_TYPE['Real Estate:CMFI'];
  }
  return [];
}

/**
 * Returns the default (admin) role for the given account type, or null when
 * the combination is unmappable.
 */
export function defaultRoleForCustomer(
  accountType: string | null | undefined,
  realEstateAccountType?: string | null,
): CustomerRbacRole | null {
  const roles = rolesForCustomer(accountType, realEstateAccountType);
  return roles[0] ?? null;
}

/**
 * True when `role` is a property-manager role (property-scoped access), for any
 * account type. Property managers are handled differently from admin-style
 * roles: their access is granted via property assignments rather than a
 * customer-user grant. Use this instead of comparing against a single role so
 * both MHP and CMFI property managers behave identically.
 */
export function isPropertyManagerRole(role: string | null | undefined): boolean {
  return role === CUSTOMER_RBAC_ROLES.MHPPropertyMgr ||
    role === CUSTOMER_RBAC_ROLES.CMFIPropertyMgr;
}

/**
 * Returns the property-manager role for a customer's account type, or null when
 * the account type has no property-manager role (e.g. Utility). Used to derive
 * the correct PM role (mhp- vs cmfi-property-mgr) for property-scoped users.
 */
export function propertyManagerRoleForCustomer(
  accountType: string | null | undefined,
  realEstateAccountType?: string | null,
): CustomerRbacRole | null {
  if (accountType === 'Real Estate') {
    if (realEstateAccountType === 'MHP') return CUSTOMER_RBAC_ROLES.MHPPropertyMgr;
    if (realEstateAccountType === 'CMFI') return CUSTOMER_RBAC_ROLES.CMFIPropertyMgr;
  }
  return null;
}

/**
 * Cognito group name for MyWaterScope end-consumers (residents). These users
 * are managed by a separate UI and are excluded from the standard Wellspring
 * user-management table. Not a member of CUSTOMER_RBAC_ROLES.
 */
export const END_CONSUMER_GROUP = 'end-consumer';

/** The signed-in Wellspring user's tier, for deciding which roles they may grant. */
export interface AssignerTier {
  isAdmin: boolean;
  isSupport: boolean;
}

/**
 * The subset of `candidateRoles` the signed-in Wellspring user may grant.
 * Today Admin and Support may grant all customer roles, so this is the identity
 * function for them and empty for anyone else. This is the single place to
 * restrict grants when elevated/granular roles arrive.
 */
export function grantableRoles(
  candidateRoles: CustomerRbacRole[],
  tier: AssignerTier,
): CustomerRbacRole[] {
  if (tier.isAdmin || tier.isSupport) return candidateRoles;
  return [];
}
